import { useState } from 'react';

import {
  SortableSettingsList,
  type SortableSettingsListItem,
} from '@/components/shared/sortable-settings-list';
import { Checkbox } from '@/components/ui/checkbox';
import { MutedText } from '@/components/ui/typography';
import type { CollectionCardField } from '@/lib/blocks/collection/blocks';
import type {
  DataWidgetFilterSelection,
  DataWidgetFilters,
} from '@/lib/blocks/collection/data-widget/blocks';
import { cn } from '@/lib/utils';
import {
  METADATA_FILTER_KIND_BY_SLOT,
  MetadataFilterPanel,
  type MetadataFilterRow,
  MetadataFilterRows,
  MetadataFilterTrigger,
} from './content-tab-metadata-filter';

export interface ContentTabFieldGroup {
  slots: string[];
}

export interface ContentTabMetadataFilter {
  slots: string[];
  standaloneRows?: MetadataFilterRow[];
  filters: DataWidgetFilters;
  onFiltersChange: (slot: string, selection: DataWidgetFilterSelection) => void;
}

type SingleRenderItem = { type: 'single'; id: string; field: CollectionCardField };
type GroupRenderItem = { type: 'group'; id: string; fields: CollectionCardField[] };
type RenderItem = SingleRenderItem | GroupRenderItem;

function buildRenderItems(
  visibleFields: CollectionCardField[],
  fieldGroups: ContentTabFieldGroup[],
): RenderItem[] {
  const slotToGroupSlots = new Map<string, string[]>();
  for (const group of fieldGroups) {
    for (const slot of group.slots) {
      slotToGroupSlots.set(slot, group.slots);
    }
  }

  const seen = new Set<string>();
  const items: RenderItem[] = [];

  for (const field of visibleFields) {
    if (seen.has(field.slot)) continue;

    const groupSlots = slotToGroupSlots.get(field.slot);
    if (groupSlots) {
      const groupFields = groupSlots
        .map((s) => visibleFields.find((f) => f.slot === s))
        .filter((f): f is CollectionCardField => f !== undefined);
      for (const gf of groupFields) seen.add(gf.slot);
      items.push({ type: 'group', id: `group:${groupSlots.join(':')}`, fields: groupFields });
    } else {
      seen.add(field.slot);
      items.push({ type: 'single', id: field.slot, field });
    }
  }

  return items;
}

function renderItemsToFields(
  newItems: RenderItem[],
  allFields: CollectionCardField[],
  currentVisibleFields: CollectionCardField[],
): CollectionCardField[] {
  const newVisible = newItems.flatMap((item) =>
    item.type === 'single' ? [item.field] : item.fields,
  );

  if (allFields.length === currentVisibleFields.length) return newVisible;

  // Re-merge static fields (not in visibleFields) back at original slots
  const visibleSlots = new Set(currentVisibleFields.map((f) => f.slot));
  const visibleQueue = [...newVisible];
  return allFields.map((f) => (visibleSlots.has(f.slot) ? (visibleQueue.shift() ?? f) : f));
}

function fieldLabel(field: CollectionCardField, forceMuted = false) {
  return (
    <span className={cn((!field.enabled || forceMuted) && 'text-muted-foreground')}>
      {field.label}
    </span>
  );
}

function fieldCheckbox(
  field: CollectionCardField,
  isDisabled: boolean,
  toggle: (slot: string) => void,
) {
  return (
    <Checkbox
      checked={field.enabled}
      onCheckedChange={isDisabled ? undefined : () => toggle(field.slot)}
      disabled={isDisabled}
      aria-label={field.label}
    />
  );
}

export function ContentTab({
  fields,
  onFieldsChange,
  hideImageField = false,
  staticImageField = false,
  disabledImageField = false,
  description = 'Choose which elements appear in your cards:',
  hiddenSlots = [],
  fieldGroups = [],
  metadataFilter,
}: {
  fields: CollectionCardField[];
  onFieldsChange: (next: CollectionCardField[]) => void;
  hideImageField?: boolean;
  staticImageField?: boolean;
  disabledImageField?: boolean;
  description?: string;
  hiddenSlots?: string[];
  fieldGroups?: ContentTabFieldGroup[];
  metadataFilter?: ContentTabMetadataFilter;
}) {
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null);
  const staticFields: CollectionCardField[] = [];
  let visibleFields = fields;

  if (hideImageField || staticImageField || disabledImageField) {
    visibleFields = visibleFields.filter((f) => f.kind !== 'image');
    if (staticImageField || disabledImageField) {
      for (const f of fields.filter((f) => f.kind === 'image')) staticFields.push(f);
    }
  }

  if (hiddenSlots.length > 0) {
    visibleFields = visibleFields.filter((field) => !hiddenSlots.includes(field.slot));
  }

  const renderItems = buildRenderItems(visibleFields, fieldGroups);

  const toggle = (slot: string) => {
    onFieldsChange(fields.map((f) => (f.slot === slot ? { ...f, enabled: !f.enabled } : f)));
  };

  const buildFieldRow = (field: CollectionCardField, isDisabled: boolean) => {
    const slot = field.slot;
    const isFilterable =
      metadataFilter?.slots.includes(slot) && slot in METADATA_FILTER_KIND_BY_SLOT;

    if (isFilterable && metadataFilter) {
      const selection = metadataFilter.filters[slot] ?? 'all';
      const expanded = expandedSlot === slot;
      return {
        label: (
          <span className="flex items-center gap-2">
            {fieldLabel(field)}
            <MetadataFilterTrigger
              kind={METADATA_FILTER_KIND_BY_SLOT[slot]}
              selection={selection}
              expanded={expanded}
              onToggle={() => setExpandedSlot(expanded ? null : slot)}
            />
          </span>
        ),
        action: fieldCheckbox(field, isDisabled, toggle),
        body: expanded ? (
          <MetadataFilterPanel
            kind={METADATA_FILTER_KIND_BY_SLOT[slot]}
            selection={selection}
            onChange={(next) => metadataFilter.onFiltersChange(slot, next)}
          />
        ) : undefined,
      };
    }

    return {
      label: fieldLabel(field),
      action: fieldCheckbox(field, isDisabled, toggle),
      body: undefined,
    };
  };

  // Accordion: image stays in staticFields with a disabled checkbox (see settings-dialog) but
  // can remain enabled:true in data — it must not count toward "last enabled field" guards.
  const fieldsForEnabledCount = disabledImageField
    ? visibleFields
    : [...staticFields, ...visibleFields];
  const enabledCount = fieldsForEnabledCount.filter((f) => f.enabled).length;

  if (visibleFields.length === 0 && staticFields.length === 0) {
    return <MutedText>No fields defined for this collection.</MutedText>;
  }

  const listItems: SortableSettingsListItem[] = [
    ...staticFields.map((field) => {
      const isDisabled = disabledImageField || (field.enabled && enabledCount === 1);
      return {
        type: 'static' as const,
        id: `static:${field.slot}`,
        label: fieldLabel(field, isDisabled),
        action: fieldCheckbox(field, isDisabled, toggle),
        className: cn('rounded-lg px-3 py-1', isDisabled && 'bg-muted opacity-60'),
      };
    }),
    ...renderItems.map((item) => {
      if (item.type === 'group') {
        return {
          type: 'group' as const,
          id: item.id,
          rows: item.fields.map((field) => {
            const isDisabled = field.enabled && enabledCount === 1;
            return { id: field.slot, ...buildFieldRow(field, isDisabled) };
          }),
          className: 'rounded-lg px-3 py-1',
        };
      }

      const isDisabled = item.field.enabled && enabledCount === 1;
      const row = buildFieldRow(item.field, isDisabled);
      return {
        type: 'sortable' as const,
        id: item.id,
        label: row.label,
        action: row.action,
        body: row.body,
        className: 'rounded-lg px-3 py-1',
      };
    }),
  ];

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{description}</p>
      <div className="space-y-2">
        {metadataFilter?.standaloneRows?.length ? (
          <MetadataFilterRows
            rows={metadataFilter.standaloneRows}
            filters={metadataFilter.filters}
            expandedSlot={expandedSlot}
            onExpandedSlotChange={setExpandedSlot}
            onChange={metadataFilter.onFiltersChange}
          />
        ) : null}
        <SortableSettingsList
          items={listItems}
          onReorder={(nextItems) => {
            const reorderedRenderItems = nextItems
              .filter((item) => item.type !== 'static')
              .map((item) => renderItems.find((renderItem) => renderItem.id === item.id))
              .filter((item): item is RenderItem => item !== undefined);

            onFieldsChange(renderItemsToFields(reorderedRenderItems, fields, visibleFields));
          }}
        />
      </div>
    </div>
  );
}
